import EverTrue from 'app';
import { createContext, useState, useEffect, useCallback, useContext, useMemo } from 'react';
import Api from 'entities/helpers/api';
import PropTypes from 'prop-types';
import ErrorLogger from 'entities/helpers/error-log-helper';

const EmailTemplatesContext = createContext({});

const EmailTemplatesProvider = ({ children, scope = 'user' }) => {
  const LIMIT = 50;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [templates, setTemplates] = useState([]);
  const [totalTemplates, setTotalTemplates] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState({});

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  const getEmailTemplates = useCallback(
    async (offset = 0, limit = LIMIT) => {
      try {
        const res = await Api.OUTREACH.EMAIL_TEMPLATES.get({
          params: {
            offset,
            limit,
            scope,
          },
        });
        return res;
      } catch (e) {
        ErrorLogger.error('Error getting Email Templates', { extra: { e, page_name: 'Email Templates Modal' } });
        throw e;
      }
    },
    [scope]
  );

  const createEmailTemplate = useCallback(async (template) => {
    setLoading(true);
    setError(null);
    try {
      const newTemplate = await Api.OUTREACH.EMAIL_TEMPLATES.post({
        data: JSON.stringify(template),
      });

      // Update local state with the new template
      setTemplates((prevTemplates) => [newTemplate, ...prevTemplates]);
      setTotalTemplates((prevTotal) => prevTotal + 1);

      EverTrue.Alert.success('Email Template created successfully');
      return newTemplate;
    } catch (e) {
      ErrorLogger.error('Error creating Email Template', { extra: { e, page_name: 'Email Templates Create Modal' } });
      setError('Failed to create email template');
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateEmailTemplate = useCallback(
    async (templateId, oid, userId, { templateName, templateSubject, templateBody, dynamicTemplate, tags }) => {
      setLoading(true);
      setError(null);
      try {
        const updatedTemplate = await Api.OUTREACH.EMAIL_TEMPLATE.put({
          urlArgs: { id: templateId },
          data: JSON.stringify({
            templateName,
            templateSubject,
            templateBody,
            dynamicTemplate,
            tags,
            oid,
            userId,
          }),
        });

        // Update local state with the updated template
        setTemplates((prevTemplates) =>
          prevTemplates.map((template) => (template.id === templateId ? updatedTemplate : template))
        );

        // Update selected template if it's the one being edited
        if (selectedTemplate.id === templateId) {
          setSelectedTemplate(updatedTemplate);
        }

        EverTrue.Alert.success('Email Template updated successfully');
        return updatedTemplate;
      } catch (e) {
        ErrorLogger.error('Error updating Email Template', { extra: { e, page_name: 'Email Templates Update Modal' } });
        setError('Failed to update email template');
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [selectedTemplate.id]
  );

  const deleteEmailTemplate = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);
      try {
        await Api.OUTREACH.EMAIL_TEMPLATE.delete({
          urlArgs: { id },
        });

        const updatedTemplates = templates.filter((template) => template.id !== id);
        setTemplates(updatedTemplates);
        setTotalTemplates(totalTemplates - 1);

        if (id === selectedTemplate.id) {
          setSelectedTemplate(updatedTemplates[0] || {});
        }

        EverTrue.Alert.success('Email Template deleted successfully');
      } catch (e) {
        ErrorLogger.error('Error deleting Email Template', { extra: { e, page_name: 'Email Templates Modal' } });
        setError('Failed to delete email template');
        EverTrue.Alert.error('There was a problem deleting the Email Template.');
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [templates, totalTemplates, selectedTemplate.id]
  );

  const searchEmailTemplates = useCallback(async (searchParam) => {
    if (!searchParam) {
      setIsSearching(false);
      setSearchResults([]);
      setSearchTerm('');
      return;
    }

    setIsSearching(true);
    setLoading(true);
    setError(null);
    setSearchTerm(searchParam);

    try {
      const res = await Api.OUTREACH.EMAIL_TEMPLATES_SEARCH.get({
        params: { template_name: searchParam },
      });
      setSearchResults(res);
      return res;
    } catch (e) {
      ErrorLogger.error('Error searching Email Templates', { extra: { e, page_name: 'Email Templates Modal' } });
      setError('Failed to search email templates');
      EverTrue.Alert.error('There was a problem searching Email Templates.');
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const resetSearch = useCallback(() => {
    setSearchTerm('');
    setIsSearching(false);
    setSearchResults([]);

    // Ensure template selection is valid after reset
    if (templates.length > 0 && (!selectedTemplate.id || !templates.some((t) => t.id === selectedTemplate.id))) {
      setSelectedTemplate(templates[0]);
    }
  }, [templates, selectedTemplate.id]);

  const handleSearch = useCallback(
    (term) => {
      // Handle empty search terms
      if (!term || term.trim() === '') {
        resetSearch();
        return;
      }

      // Only proceed with non-empty search terms
      searchEmailTemplates(term);
    },
    [searchEmailTemplates, resetSearch]
  );

  const fetchTemplates = useCallback(
    async (offset = 0) => {
      // Don't fetch when searching
      if (isSearching) {
        return Promise.resolve();
      }

      // Only set loading to true on initial load, not during pagination
      if (offset === 0) {
        setLoading(true);
      }

      setError(null);

      try {
        const { items = [], total } = await getEmailTemplates(offset, LIMIT);

        // Always update total
        setTotalTemplates(total);

        // For initial load, replace templates
        // For pagination, append to existing templates
        if (offset === 0) {
          setTemplates(items);

          // Select first template if none is selected and we have templates
          if (!selectedTemplate.id && items.length > 0) {
            setSelectedTemplate(items[0]);
          }
        } else {
          setTemplates((prevTemplates) => [...prevTemplates, ...items]);
        }

        return { items, total };
      } catch (e) {
        setError('Failed to fetch email templates');
        ErrorLogger.error('Error getting Email Templates', { extra: { e, page_name: 'Email Templates Modal' } });
        EverTrue.Alert.error('There was a problem getting Email Templates.');
        return Promise.reject(e);
      } finally {
        setLoading(false);
      }
    },
    [getEmailTemplates, isSearching, selectedTemplate.id]
  );

  // Initialize templates on mount
  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Optimized handler for template selection to prevent unnecessary re-renders
  const handleSelectTemplate = useCallback(
    (template) => {
      if (!template || template.id === selectedTemplate.id) return;

      // Important: Don't set loading state when selecting a template
      setSelectedTemplate(template);
    },
    [selectedTemplate.id]
  ); // Remove the selectedTemplate.id dependency to prevent re-renders

  // Memoize so context consumers (e.g. EmailTemplateCard) only re-render when
  // a value they actually use changes, allowing the cards' React.memo to skip
  // the rest of the list during a selection.
  const contextValue = useMemo(
    () => ({
      // States
      templates,
      searchResults,
      totalTemplates,
      selectedTemplate,
      loading,
      error,
      searchTerm,
      isSearching,
      // Actions
      setSelectedTemplate: handleSelectTemplate,
      createEmailTemplate,
      updateEmailTemplate,
      deleteEmailTemplate,
      fetchTemplates,
      handleSearch,
      resetSearch,
    }),
    [
      templates,
      searchResults,
      totalTemplates,
      selectedTemplate,
      loading,
      error,
      searchTerm,
      isSearching,
      handleSelectTemplate,
      createEmailTemplate,
      updateEmailTemplate,
      deleteEmailTemplate,
      fetchTemplates,
      handleSearch,
      resetSearch,
    ]
  );

  return <EmailTemplatesContext.Provider value={contextValue}>{children}</EmailTemplatesContext.Provider>;
};

const useEmailTemplatesProvider = () => {
  const context = useContext(EmailTemplatesContext);
  if (!context) {
    throw new Error('useEmailTemplatesProvider must be used within a EmailTemplatesProvider');
  }
  return context;
};

EmailTemplatesProvider.propTypes = { children: PropTypes.node, scope: PropTypes.string };

export { EmailTemplatesProvider, EmailTemplatesContext, useEmailTemplatesProvider };
